import { useEffect, useMemo } from 'react';
import { TextureLoader } from 'three';
import * as THREE from 'three';
// import { getMaterialsData } from "../dbApis";

/**
 * Custom hook for applying and updating textures to a material based on provided finish data.
 *
 * This hook:
 * - Finds the appropriate finish data from a predefined list.
 * - Loads textures asynchronously using the `TextureLoader`.
 * - Applies loaded textures and updates material properties such as color, roughness, and metalness.
 * - Resets material properties when no texture URL is provided.
 * - Cleans up by disposing of textures when the component unmounts or dependencies change.
 *
 * @param {THREE.Material} material - The material to which textures and properties will be applied.
 * @param {string} finishes - ID of the finish data to be applied.
 * @returns {THREE.TextureLoader} - The texture loader instance used for loading textures.
 */

export default function useSetMaterialFinish(
  material,
  finishes,
  setLoading
) {

  const { diffuseMap, aoMap, normalMap,roughness, repeat, rotation, normalScale, aoMap1,color } = finishes;
  // console.log(material,"materialalalalala")

  // Memoize the TextureLoader to avoid recreating it on every render
  const loader = useMemo(() => new TextureLoader(), []);

  // console.log("loading material", setLoading)

  useEffect( () => {
    // Exit early if material is not provided
    if (!material) return;
    setLoading?.(true)

    // Array to keep track of loaded textures
    const textures = [];

    // Function to load a texture asynchronously
    const loadTextureAsync = async (url, settings, isSrgb) => {
      return new Promise((resolve) => {
        loader.load(url, (texture) => {
          // Set texture properties
          texture.flipY = false;
          if (isSrgb) {
            texture.colorSpace = THREE.SRGBColorSpace;
          }
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.repeat.set(settings[0], settings[1]);
          texture.rotation =  rotation ? THREE.MathUtils.degToRad(rotation): THREE.MathUtils.degToRad(0);
          texture.roughness = roughness ? roughness : 1 ;

          // Add texture to the array and resolve the promise
          textures.push(texture);
          resolve(texture);
        });
      });
    };

    // Function to update the material with textures and properties
    const updateMaterialTextures = async () => {
      // Array to hold promises for loading textures
      const texturePromises = [];

      // Reset material properties if the material name does not match the check list
      material.map = null;
      material.roughnessMap = null;
      material.metalnessMap = null;
      material.normalMap = null;
      material.roughness = 1; 
      material.metalness = 0;
      material.aoMap = null;
      material.displacementMap = null;
      material.clearcoat=0;
      material.clearcoatRoughness=0;
      material.ior = 1; // Default index of refraction value
      material.color = null;

      // Load and apply diffuse texture if URL is provided
      if (diffuseMap) {
        texturePromises.push(
          loadTextureAsync(diffuseMap, repeat ? repeat : [1, 1], true).then(
            (texture) => {
              material.map = texture;
              // material.color = new THREE.Color('#ddd');
            },
          ),
        );
      }

      if (color) {
        material.color = new THREE.Color(color);
      }

      if (normalMap) {
        texturePromises.push(
          loadTextureAsync(
            normalMap,
            repeat ? repeat : [1, 1],
            true,
          ).then((texture) => {
            material.normalMap = texture;
            if(normalScale){
              material.normalScale.set(normalScale[0], normalScale[1]);
            }
          }),
        );
      }
      // console.log(material,"material")

      if (aoMap) {
        texturePromises.push(
          loadTextureAsync(
            aoMap,
            [1, 1],
            true
          ).then((texture) => {
            material.aoMap = texture;
            material.aoMapIntensity = 1;
            material.aoMap.offset.set(0, 0);
          }),
        );
      }

      if (aoMap1) {
        texturePromises.push(
          loadTextureAsync(
            aoMap1,
            [1, 1],
            false,
          ).then((texture) => {
            material.aoMap = texture;
            material.aoMapIntensity = 1;
            material.aoMap.offset.set(0, 0);
            material.aoMap.channel = 1;
          }),
        );
      }

      // Wait for all textures to be loaded and applied
      await Promise.all(texturePromises);

      // Mark material as needing update to reflect changes
      material.needsUpdate = true;

      setLoading?.(false)
    };

    // Call the function to update material textures
    updateMaterialTextures();

    // Cleanup function to dispose of textures when component unmounts or dependencies change
    return () => {
      textures.forEach((texture) => {
        texture.dispose(); // Dispose of each texture
      });
    };
  }, [
    material,
    diffuseMap,
    aoMap,
    aoMap1,
    normalMap,
    repeat,
    rotation,
    color,
    roughness,
  ]);

  return loader;
}
